import { css, useTheme } from '@emotion/react';

import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Suspense, useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';

import { useUserQuery } from '@hooks/query/useUserQuery';
import { useModal } from '@hooks/useModal';
import { Spacing } from 'chatie-design-system';
import {
  Bell,
  BellFill,
  BellStroke,
  Face1,
  Face2,
  Face3,
  Face4,
  Face5,
  Face6,
  Icon,
  VerticalEllipsis,
} from 'chatie-design-system/icon';

import CharacterCardList from '@components/characters/CharacterCardList';
import ActionSheet, { ActionSheetOption } from '@components/common/ActionSheet';
import Loading from '@components/common/Loading';
import Seo from '@components/common/Seo';
import Tab, { useTab } from '@components/common/Tab';
import PostList from '@components/community/PostList';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import SafeArea from '@components/layout/SafeArea';
import LiveSessionCard from '@components/live/LiveSessionCard';
import NotificationModal from '@components/modal/NotificationModal';
import RestrictionInfoFullscreenModal from '@components/modal/RestrictionInfoFullscreenModal';
import StoryList from '@components/stories/StoryList';
import UserInfo from '@components/user/UserInfo';

import { fetchUserLiveSessions } from '@api/live';
import { fetchUserRestriction, postToggleBlockUser } from '@api/user';
import {
  LIVE_FEATURE_ENABLED,
  QUERY_KEYS,
  ReportObjectType,
  RestrictionStatus,
  SettingType,
} from '@constants';
import { withAuth } from '@contexts/auth';
import { useGlobalContext } from '@contexts/global';
import { IUserDetail } from '@model';
import { media, responsiveMaxWidth } from '@styles/css';
import { useNotification, useScreen } from '@utils/hook';
import { toQueryString } from '@utils/misc';
import { getRouterQuery, useOptimisticUpdate } from '@utils/query';

const userDetailContainerCSS = css`
  ${responsiveMaxWidth};
  margin: 0 auto;

  ${media.tablet} {
    padding-left: ${Spacing.xlarge}px;
    padding-right: ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    padding-left: ${Spacing.xxlarge}px;
    padding-right: ${Spacing.xxlarge}px;
  }
`;

/**
 * Page
 */
type UserAction = 'report' | 'block' | 'unblock';

type UserTab = 'stories' | 'play' | 'community';

const UserDetail = () => {
  const { t } = useTranslation();
  const { isCheming } = useGlobalContext();
  const router = useRouter();
  const theme = useTheme();
  const userId = getRouterQuery(router.query.userId) as number;

  const { useFetchUser } = useUserQuery();
  const { data: user } = useFetchUser(userId);
  const { data: liveSessions } = useQuery(
    QUERY_KEYS.LIVE.USER(userId),
    () => fetchUserLiveSessions(userId),
    {
      enabled: LIVE_FEATURE_ENABLED && !!userId && !isCheming,
      suspense: false,
      retry: false,
      refetchOnWindowFocus: 'always',
    }
  );
  const { data: restriction } = useQuery(['userRestriction', userId], () =>
    fetchUserRestriction(userId)
  );
  useScreen('작가 상세');
  const { data: userSetting } = useNotification(
    SettingType.user,
    userId,
    'followingUpdatePushStatus',
    user ? user.isFollowing : false
  );
  const notificationOption =
    userSetting?.setting['followingUpdatePushStatus'] ?? 'partial_enable';

  const tabs = useTab<UserTab>(
    [
      { label: t('작품'), value: 'stories' },
      { label: t('플레이'), value: 'play' },
      { label: t('커뮤니티'), value: 'community' },
    ],
    { replace: true }
  );
  const { activeTab } = tabs;

  const [
    isRestrictionInfoModalOpen,
    { open: openRestrictionInfoModal, close: closeRestrictionInfoModal },
  ] = useModal('restriction-info-modal');
  const [
    isNotificationModalOpen,
    { open: openNotificationModal, close: closeNotificationModal },
  ] = useModal('notification-modal');

  const actions: ActionSheetOption<UserAction>[] = [
    { label: t('신고하기'), value: 'report' },
    user?.isBlocked
      ? { label: t('차단 해제하기'), value: 'unblock' }
      : { label: t('차단하기'), value: 'block' },
  ];

  const optimistic = useOptimisticUpdate();
  const { mutate: toggleBlockUser } = useMutation(postToggleBlockUser);

  const handleAction = useCallback(
    (value: UserAction) => {
      switch (value) {
        case 'report':
          router.push(
            `/report${toQueryString({
              objectType: ReportObjectType.user,
              objectId: userId,
            })}`
          );
          break;
        case 'block':
        case 'unblock':
          toggleBlockUser(userId, {
            onSuccess: () => {
              optimistic.update<IUserDetail>(
                QUERY_KEYS.USER.ONE(userId),
                (user) => ({ ...user, isBlocked: !user.isBlocked })
              );
            },
          });
          break;
        default:
          break;
      }
    },
    [optimistic, router, toggleBlockUser, userId]
  );

  const restrictionIcons = {
    [RestrictionStatus.normal]: Face1,
    [RestrictionStatus.caution]: Face2,
    [RestrictionStatus.warn1]: Face3,
    [RestrictionStatus.warn2]: Face4,
    [RestrictionStatus.warn3]: Face5,
    [RestrictionStatus.warn4]: Face6,
  };
  const notificationIconMapper = {
    enable: BellFill,
    disable: BellStroke,
    partial_enable: Bell,
    default: Bell,
  };

  const isNotificationDisabled =
    notificationOption === 'default' || notificationOption === 'disable';

  return (
    <div css={userDetailContainerCSS}>
      <Seo title={user?.nickname} />

      <Header
        right={(color) => (
          <>
            {restriction?.status !== RestrictionStatus.exception &&
              !isCheming && (
                <button className="icon-btn" onClick={openRestrictionInfoModal}>
                  <Icon
                    icon={
                      restrictionIcons[
                        restriction?.status ?? RestrictionStatus.normal
                      ]
                    }
                    color={color}
                  />
                </button>
              )}
            {user?.isFollowing && !isCheming && (
              <button className="icon-btn" onClick={openNotificationModal}>
                <Icon
                  icon={notificationIconMapper[notificationOption]}
                  color={
                    isNotificationDisabled ? color : theme.colors.yellow500
                  }
                />
              </button>
            )}
            {!isCheming && (
              <ActionSheet
                name="user-action"
                options={actions}
                onSelect={handleAction}
              >
                {(open) => (
                  <button className="icon-btn" onClick={open}>
                    <Icon icon={VerticalEllipsis} color={color} />
                  </button>
                )}
              </ActionSheet>
            )}
          </>
        )}
      />

      <UserInfo userId={userId} />

      {LIVE_FEATURE_ENABLED && liveSessions?.[0] && (
        <LiveSessionCard session={liveSessions[0]} />
      )}

      {!isCheming && <Tab {...tabs} fit />}

      <Suspense fallback={<Loading push />}>
        {activeTab === 'stories' && !isCheming && <StoryList userId={userId} />}
        {activeTab === 'play' && !isCheming && (
          <CharacterCardList userId={userId} isMyPage={false} />
        )}
        {activeTab === 'community' && !isCheming && (
          <PostList userId={userId} />
        )}
        {isCheming && <CharacterCardList userId={userId} isMyPage={false} />}
      </Suspense>

      <RestrictionInfoFullscreenModal
        open={isRestrictionInfoModalOpen}
        onClose={closeRestrictionInfoModal}
      />

      <NotificationModal
        name="notification-modal"
        open={isNotificationModalOpen}
        objectType={SettingType.user}
        objectId={userId}
        settingKey="followingUpdatePushStatus"
        onClose={closeNotificationModal}
        notificationOption={notificationOption}
      />
    </div>
  );
};

const UserDetailPage: NextPage = () => {
  return (
    <Layout>
      <Suspense fallback={<Loading center />}>
        <SafeArea bottom>
          <UserDetail />
        </SafeArea>
      </Suspense>
    </Layout>
  );
};

export default withAuth(UserDetailPage, { isSignInRequired: false });
